import React, { useRef, useState } from "react";
import CardForm from "../../components/CardForm";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "../../hooks/queryParams";
import queryString from "query-string";
import { Button, Container, useDisclosure, useToast } from "@chakra-ui/react";
import {
  SignatureBackground,
  useDeleteSignatureBackgroundMutation,
  useSetDefaultSignatureBackgroundMutation,
} from "../../generated/graphql";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { AddIcon } from "@chakra-ui/icons";
import TableListTitle from "../../components/TableList/TableListTitle";
import { TableImperativeHandle } from "../../components/DefaultTable";
import { FormMutationType } from "../../components/FullWidthModal/interface";
import { IFilterSignatureBackground } from "../../components/TableSignatureBackground/interface";
import TableSignatureBackground from "../../components/TableSignatureBackground";
import SignatureForm from "./SignatureBackgroundForm";
import { useErrorMessage } from "../../hooks/errorMessage";

const ListSignatureBackground: React.FC = () => {
  const location = useLocation();

  const { pathname } = location;

  const navigate = useNavigate();

  const toast = useToast();

  const {
    isOpen: isOpenForm,
    onOpen: onOpenForm,
    onClose: onCloseForm,
  } = useDisclosure();

  const [selectedId, setSelectedId] = useState("");

  const { confirmation, setIsLoading, close } = useConfirmation();

  const { queryParams } = useQueryParams<IFilterSignatureBackground & { page: string }>();

  const onPageChange = (page: number) => {
    const url = queryString.stringifyUrl({
      url: pathname,
      query: {
        ...queryParams,
        page,
      },
    });

    navigate(url);
  };

  const onFilterSubmit = (params: IFilterSignatureBackground) => {
    const url = queryString.stringifyUrl({
      url: pathname,
      query: {
        ...queryParams,
        ...params,
        page: 1,
      },
    });
    navigate(url);
  };

  const [deleteSignatureBackgroundMutation] = useDeleteSignatureBackgroundMutation();

  const onDelete = (row: SignatureBackground) => {
    confirmation({
      title: "Confirmation",
      description: `Are you sure want to delete this signature background?`,
      callback: async () => {
        try {
          setIsLoading(true);
          await deleteSignatureBackgroundMutation({
            variables: { id: row.id },
          });
          tableRef.current?.refresh();
          toast({
            status: "success",
            description: "Successfully deleted!",
          });
        } catch (e: any) {
          if (e.message === "cannot_delete_the_only_one") {
            toast({
              status: "error",
              description: "You should have minimal 1 Signature Background!",
            });
          } else if (e.message === "constraint_violation") {
            toast({
              status: "error",
              description: "Cannot delete in used Signature Background!",
            });
          } else {
            toast({
              status: "error",
              description: e.message,
            });
          }
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  const tableRef = useRef<TableImperativeHandle<SignatureBackground>>(null);

  const { showErrorMessage } = useErrorMessage();

  const [setDefault] = useSetDefaultSignatureBackgroundMutation();
  const onChangeDefault = (row: SignatureBackground) => {
    confirmation({
      title: "Confirmation",
      description: `Are you sure want to change the default?`,
      callback: async () => {
        try {
          setIsLoading(true);
          await setDefault({
            variables: { id: row.id },
          });
          tableRef.current?.refresh();
          toast({
            status: "success",
            description: "Successfully Updated!",
          });
        } catch (e) {
          showErrorMessage(e);
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  return (
    <>
      <Container maxW="container.xl">
        <CardForm>
          <TableListTitle
            title="List Signature Backgrounds"
            actionComponents={
              <Button
                onClick={() => onOpenForm()}
                leftIcon={<AddIcon />}
                size="sm"
              >
                Create New Background
              </Button>
            }
          />

          <TableSignatureBackground
            ref={tableRef}
            page={
              queryParams?.page
                ? parseInt(queryParams.page as unknown as string)
                : 1
            }
            onChangeDefault={onChangeDefault}
            onPageChange={onPageChange}
            filterParams={queryParams}
            onFilterSubmit={onFilterSubmit}
            hasMenu
            onDelete={onDelete}
            onEdit={(row) => {
              setSelectedId(row.id);
              onOpenForm();
            }}
          />
        </CardForm>
      </Container>
      <SignatureForm
        isOpen={isOpenForm}
        onClose={(res) => {
          if (res?.type === FormMutationType.UPDATE) {
            tableRef.current?.updateRow(res.data as SignatureBackground);
          } else if (res?.type === FormMutationType.CREATE) {
            tableRef.current?.refresh();
            onPageChange(1);
          }
          setSelectedId("");
          onCloseForm();
        }}
        id={selectedId}
      />
    </>
  );
};

export default ListSignatureBackground;
